import { Platform, StyleSheet, type StyleProp, type TextStyle } from 'react-native';

/**
 * How far under its line height each line is set on iOS. Invisible (a 10-line
 * paragraph ends 0.05pt short) but far above Yoga's float32 error, which is
 * about 0.0001pt a few thousand points down a page.
 */
export const LINE_HEIGHT_TRIM = 0.005;

/**
 * Works around RN iOS dropping the last line of wrapped text
 * (facebook/react-native#53450). RN measures text and rounds the height up to
 * the next pixel, then Yoga snaps the frame's edges to the pixel grid in
 * float32, which can leave the frame ~0.0001pt shorter than measured (65.9999
 * for 3 x 22pt). TextKit only draws lines that fully fit, so it drops the last
 * line and crams its words into the one above. Where this lands depends on the
 * text's position on screen, so it shows up on some phones and not others.
 *
 * Trimming the line height a hair keeps the real text height just under a
 * pixel boundary: RN's round-up then always leaves room for the frame error.
 * Padding can't do this: it comes off the drawing box exactly. Font-default
 * line heights are almost never whole pixels, which is why apps that don't set
 * lineHeight rarely see this; ours sets whole-point line heights everywhere.
 */
export function fitLineHeight(style: StyleProp<TextStyle>): StyleProp<TextStyle> {
  if (Platform.OS !== 'ios') return style;
  const lineHeight = StyleSheet.flatten(style)?.lineHeight;
  return lineHeight ? [style, { lineHeight: lineHeight - LINE_HEIGHT_TRIM }] : style;
}
